/** Short JWT / API codes for clash winner badge, optionally with style: c | c#8. */
export const CLASH_WINNER_ICON_CROWN = 'c';
export const CLASH_WINNER_ICON_L = 'l';
export const CLASH_WINNER_ICON_FLEX = 'f';

const VALID_CODES = new Set([
  CLASH_WINNER_ICON_CROWN,
  CLASH_WINNER_ICON_L,
  CLASH_WINNER_ICON_FLEX
]);

/**
 * @param {unknown} rawCode
 * @returns {'c'|'l'|'f'|null}
 */
function normalizeWinnerCode(rawCode) {
  if (rawCode === CLASH_WINNER_ICON_L || rawCode === 'l') return CLASH_WINNER_ICON_L;
  if (rawCode === CLASH_WINNER_ICON_FLEX || rawCode === 'f' || rawCode === 'flex') {
    return CLASH_WINNER_ICON_FLEX;
  }
  if (rawCode === CLASH_WINNER_ICON_CROWN || rawCode === 'crown') return CLASH_WINNER_ICON_CROWN;
  return null;
}

/**
 * @param {unknown} stored - JWT `w` or API `clashWinnerIcon` / `winnerIcon`
 * @returns {{ code: 'c'|'l'|'f', styleId: number|null }|null}
 */
export function parseClashWinnerIcon(stored) {
  if (stored == null || stored === '') return null;
  if (typeof stored !== 'string') return null;
  const hash = stored.indexOf('#');
  let codePart = stored;
  let styleId = null;
  if (hash >= 0) {
    codePart = stored.slice(0, hash);
    const stylePart = stored.slice(hash + 1);
    if (!/^\d+$/.test(stylePart)) return null;
    const n = parseInt(stylePart, 10);
    if (!Number.isInteger(n) || n <= 0) return null;
    styleId = n;
  }
  const code = normalizeWinnerCode(codePart);
  if (!code) return null;
  return { code, styleId };
}

/**
 * @param {'c'|'l'|'f'} code
 * @param {number|null|undefined} styleId
 * @returns {string}
 */
export function formatClashWinnerIcon(code, styleId) {
  const c = VALID_CODES.has(code) ? code : CLASH_WINNER_ICON_CROWN;
  if (styleId != null && styleId !== 0) {
    const n = Number(styleId);
    if (Number.isInteger(n) && n > 0) return `${c}#${n}`;
  }
  return c;
}

/**
 * @param {unknown} stored
 * @returns {string} e.g. 'c' | 'l' | 'f' | 'c#8'
 */
export function sanitizeClashWinnerIcon(stored) {
  const parsed = parseClashWinnerIcon(stored);
  if (!parsed) return CLASH_WINNER_ICON_CROWN;
  return formatClashWinnerIcon(parsed.code, parsed.styleId);
}

/**
 * @param {unknown} stored
 * @returns {string} 'crown' | 'l' | 'f' | 'crown#8' | …
 */
export function clashWinnerIconToApi(stored) {
  const parsed = parseClashWinnerIcon(stored) ?? { code: CLASH_WINNER_ICON_CROWN, styleId: null };
  let apiCode = 'crown';
  if (parsed.code === CLASH_WINNER_ICON_L) apiCode = 'l';
  else if (parsed.code === CLASH_WINNER_ICON_FLEX) apiCode = 'f';
  if (parsed.styleId != null) return `${apiCode}#${parsed.styleId}`;
  return apiCode;
}

/**
 * @param {unknown} stored
 * @returns {'c'|'l'|'f'}
 */
export function clashWinnerIconCode(stored) {
  return (parseClashWinnerIcon(stored) ?? { code: CLASH_WINNER_ICON_CROWN }).code;
}

/**
 * Public asset path for a winner badge.
 * @param {unknown} winnerIcon - 'crown' | 'l' | 'f' | 'c' | 'flex' | 'c#8' | 'crown#8' | …
 * @returns {string}
 */
export function clashWinnerIconSrc(winnerIcon) {
  const parsed = parseClashWinnerIcon(winnerIcon) ?? {
    code: CLASH_WINNER_ICON_CROWN,
    styleId: null
  };
  if (parsed.styleId != null) {
    return `/clash-winners/${parsed.code}/styles/${parsed.styleId}.svg`;
  }
  return `/clash-winners/${parsed.code}.svg`;
}
